import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom'

import { ROUTES } from 'routes'

import { useCompanyRoles, CompanyRoleListItem } from 'hooks/api'
import { useCanAccess, useReferrerState } from 'hooks'
import { useSnackbar } from 'contexts'
import { getRoleDisplayName, getRoleStatus } from 'constants/roles'
import { ROLE_STATUSES } from 'constants/statuses'

import {
  InfoIcon,
  PencilIcon,
  CopyIcon,
  Trash2Icon,
  ShieldIcon,
  UserPlusIcon,
} from 'lucide-react'

import { Box, Stack, Typography, Link } from '@mui/material'

import { Button, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import {
  Header,
  Table,
  Loader,
  EmptyState,
  ActionDropdown,
  AssignUsersModal,
  CreateRoleModal,
  RenameRoleModal,
  DuplicateRoleModal,
  DeleteRoleModal,
  PermissionsMatrix,
  StatusChip,
  UserGroupCell,
  InfoTooltip,
} from 'components'

import type { GridColDef } from '@mui/x-data-grid-pro'

// ── Segmented control tabs ────────────────────────────────────────────────────
const TABS = [
  { label: 'Roles', value: 'roles' },
  { label: 'Permissions', value: 'permissions' },
]

// ── Component ─────────────────────────────────────────────────────────────────
export const RolesPage = () => {
  const navigate = useNavigate()
  // Referrer state handed to the role drill-in so its Header offers "← Back to Roles".
  const referrer = useReferrerState()

  // Segmented-control state
  const [activeTab, setActiveTab] = useState('roles')

  // Modal state
  const [openAddModal, setOpenAddModal] = useState(false)

  // Open the "Add role" modal when arriving with ?create=1 (e.g. from the
  // assign-role modal's "Create custom role" link), then strip the param.
  //
  // `state: location.state` is load-bearing, not defensive. `setSearchParams` IS
  // a navigation, and a navigation with no `state` replaces the entry's state
  // with undefined — so stripping the param silently discarded the `{ backTo,
  // backLabel }` referrer the caller handed us (§42). The visible symptom was
  // "← Back to Users" rendering on first paint and then vanishing the instant
  // this effect ran. Re-passing the current state keeps the entry intact while
  // only the query string changes.
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setOpenAddModal(true)
      searchParams.delete('create')
      setSearchParams(searchParams, { replace: true, state: location.state })
    }
  }, [searchParams, setSearchParams, location.state])
  const [selectedRole, setSelectedRole] = useState<CompanyRoleListItem | null>(null)
  const [openAssignModal, setOpenAssignModal] = useState(false)
  const [openRenameModal, setOpenRenameModal] = useState(false)
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  const { data: roles = [], isLoading, refetch } = useCompanyRoles()

  // Permission gate for member management actions
  const { isHasAccess: canManageMembers } = useCanAccess({ action: 'assign_users' })

  // ── Action handlers ──────────────────────────────────────────────────────
  const handleViewRole = useCallback((role: CompanyRoleListItem) => {
    navigate(ROUTES.SETTINGS_COMPANY_ROLE(role.name), { state: referrer })
  }, [navigate, referrer])

  const handleAssignUsers = useCallback((role: CompanyRoleListItem) => {
    setSelectedRole(role)
    setOpenAssignModal(true)
  }, [])

  const handleRename = useCallback((role: CompanyRoleListItem) => {
    setSelectedRole(role)
    setOpenRenameModal(true)
  }, [])

  const handleDuplicate = useCallback((role: CompanyRoleListItem) => {
    setSelectedRole(role)
    setOpenDuplicateModal(true)
  }, [])

  const handleDelete = useCallback((role: CompanyRoleListItem) => {
    setSelectedRole(role)
    setOpenDeleteModal(true)
  }, [])

  const getActionItems = useCallback((role: CompanyRoleListItem) => {
    const items: any[] = [
      {
        label: 'Details',
        icon: <InfoIcon strokeWidth={1} />,
        onClick: () => handleViewRole(role),
      },
    ]

    // §27: an in-context action stays VISIBLE and goes disabled with a tooltip naming
    // the missing permission, rather than vanishing — hiding it left no path to
    // discover or request access, and made the kebab read differently per role.
    items.push({
      label: 'Assign users',
      icon: <UserPlusIcon strokeWidth={1} />,
      onClick: () => handleAssignUsers(role),
      disabled: !canManageMembers,
      tooltip: canManageMembers
        ? undefined
        : "You need the 'Assign users' permission. Ask an admin to grant it.",
    })

    if (role.isCustom) {
      items.push({
        label: 'Rename',
        icon: <PencilIcon strokeWidth={1} />,
        onClick: () => handleRename(role),
      })
    }

    items.push({
      label: 'Duplicate',
      icon: <CopyIcon strokeWidth={1} />,
      onClick: () => handleDuplicate(role),
    })

    if (role.isCustom) {
      items.push({
        label: 'Delete',
        icon: <Trash2Icon strokeWidth={1} />,
        destructive: true,
        divider: true,
        onClick: () => handleDelete(role),
      })
    }

    return items
  }, [handleViewRole, handleAssignUsers, handleRename, handleDuplicate, handleDelete, canManageMembers])

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = useMemo<GridColDef<CompanyRoleListItem>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Role',
        // §44/§59 — the column names a term of art, and nothing on this page says what a
        // role IS (that permissions are granted through it, never to a person). `headerName`
        // stays for sorting, the column menu and the toolbar's column list; `renderHeader`
        // only changes what is painted. 320px leaves room for the label, the sort icon and
        // the glyph without truncating (§49).
        renderHeader: () => (
          <Stack direction="row" alignItems="center">
            <span>Role</span>
            <InfoTooltip term="role" />
          </Stack>
        ),
        // Fixed width (no flex) so the Users column sits near the table centre,
        // matching the Figma layout, instead of being pushed to the far right.
        width: 320,
        // Search + sort operate on the displayed name (e.g. "Admin"), not the raw slug.
        valueGetter: (_value: unknown, row: CompanyRoleListItem) => getRoleDisplayName(row),
        // Main clickable cell: navigates to the role detail page, matching the
        // link-cell convention used by every other data table (UserCell/CompanyCell).
        renderCell: ({ row }) => (
          <Link
            component={RouterLink}
            to={ROUTES.SETTINGS_COMPANY_ROLE(row.name)}
            state={referrer}
            underline="none"
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <Typography variant="body2">{getRoleDisplayName(row)}</Typography>
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 184,
        sortable: false,
        valueGetter: (_v: unknown, row: CompanyRoleListItem) => ROLE_STATUSES[getRoleStatus(row)].label,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
            <StatusChip statuses={ROLE_STATUSES} status={getRoleStatus(row)} size="small" />
          </Stack>
        ),
      },
      {
        field: 'users',
        headerName: 'Users',
        // Wide enough to always show 5 circles (4 avatars + the "+N").
        width: 240,
        sortable: false,
        valueGetter: (_v: unknown, row: CompanyRoleListItem) => row.memberCount ?? 0,
        renderCell: ({ row }) => {
          const members = row.members ?? []
          if (members.length === 0) {
            return (
              <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
                <Typography variant="body2" color="text.disabled"></Typography>
              </Stack>
            )
          }
          // Stacked avatars + "+N" overflow via the shared UserGroupCell.
          const users = members.map((m) => ({
            id: m.userId,
            first_name: m.firstName,
            last_name: m.lastName,
            email: m.email,
          }))
          return (
            <UserGroupCell
              users={users as any}
              route={ROUTES.SETTINGS_COMPANY_USER}
              size={40}
              align="left"
              max={5}
            />
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    [getActionItems, referrer]
  )

  // ── Render ────────────────────────────────────────────────────────────────

  // The Roles/Permissions toggle is view-navigation, so it sits next to the title
  // (leftComponents); the top-right stays for actions (the primary "+ Add role").
  return (
    <>
      <Header
        title="Roles &amp; Permissions"
        // `nested`: this Header sits under the settings page's own "Settings" header, so the
        // wayfinding row it would otherwise reserve is empty space that pushed this title 20px
        // below the non-selfTitled sections' (145.9px vs 125.9px, measured).
        nested
        rightComponents={
          <Button
            variant="primary"
            disableElevation
            onClick={() => setOpenAddModal(true)}
          >
            + Add role
          </Button>
        }
      />
      {/* VRG-470: underline page-header tabs. `redirect={false}` + a controlled value —
          these values are state keys, not routes. Gap below from TABS_CONTENT_GAP. */}
      <Tabs
        redirect={false}
        tabs={TABS}
        value={activeTab}
        onChange={setActiveTab}
        sx={{ mb: `${TABS_CONTENT_GAP}px` }}
      />

      {activeTab === 'permissions' ? (
        <Box data-tour="roles-matrix">
          <PermissionsMatrix />
        </Box>
      ) : isLoading ? (
        <Loader />
      ) : (
        <Table
          topOffset={324}
          fillBottom
          rows={roles}
          columns={columns}
          showSelection={false}
          emptyState={
            <EmptyState
              title="No roles found"
              text="Create a role using the button above."
              icon={<ShieldIcon strokeWidth={1} size={32} />}
            />
          }
        />
      )}

      {/* ── Modals (Add is reachable from both tabs) ───────────────────────── */}
      <AssignUsersModal
        role={selectedRole}
        open={openAssignModal}
        onClose={() => setOpenAssignModal(false)}
        onAssigned={() => refetch()}
      />
      <CreateRoleModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        // Refresh the list, then jump straight to the new role's page so the
        // user can configure its permissions (the modal's "Next" action).
        onCreated={(role) => {
          refetch()
          navigate(ROUTES.SETTINGS_COMPANY_ROLE(role.name))
        }}
      />
      <RenameRoleModal
        role={selectedRole}
        open={openRenameModal}
        onClose={() => setOpenRenameModal(false)}
        onRenamed={() => refetch()}
      />
      <DuplicateRoleModal
        role={selectedRole}
        open={openDuplicateModal}
        onClose={() => setOpenDuplicateModal(false)}
        onDuplicated={() => refetch()}
      />
      <DeleteRoleModal
        role={selectedRole}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onDeleted={() => refetch()}
      />
    </>
  )
}
